/**
 * Feed-scope accumulator for one bootstrap run. Incremental (warm) boots use it to scope the
 * post-bootstrap recompute to feeds the bootstrap deltas actually touched instead of re-running
 * the full-table unread/alias/activity recomputation on every boot (WEB-2202). Cold boots ignore
 * it and keep the full recompute.
 */
export type BootstrapDelta = {
  /** Feeds named by the run's feed, permission, item, and event responses. */
  feedIds: Set<string>;
  /**
   * True when the delta carried feeds, permissions, or command aliases — the tables that define
   * alias-feed metadata. Item rows don't count: `bulkUpsertItemAndCalculateMembership` already
   * computes per-item alias/bot membership and feed latest-activity at write time.
   */
  aliasMetadataTouched: boolean;
};

export function emptyBootstrapDelta(): BootstrapDelta {
  return { feedIds: new Set(), aliasMetadataTouched: false };
}

const ALIAS_METADATA_TABLES = new Set(["feed", "permission", "workspaceCommandAlias"]);
const FEED_ID_TABLES = new Set(["permission", "item", "accountEvent", "workspaceCommandAlias"]);

type BootstrapRow = { id?: string; feedId?: string | null };

/** Folds one bootstrap response page into the run's delta. */
export function accumulateBootstrapDelta(
  delta: BootstrapDelta,
  tableName: string,
  rows: readonly BootstrapRow[],
): void {
  if (rows.length === 0) return;

  if (ALIAS_METADATA_TABLES.has(tableName)) {
    delta.aliasMetadataTouched = true;
  }

  if (tableName === "feed") {
    for (const row of rows) {
      if (row.id) delta.feedIds.add(row.id);
    }
    return;
  }

  if (FEED_ID_TABLES.has(tableName)) {
    for (const row of rows) {
      if (row.feedId) delta.feedIds.add(row.feedId);
    }
  }
}
